import React, { useEffect, useRef, useState } from 'react';
import Input from './Input';
import Textarea from './Textarea';
import SectionHeading from './SectionHeading';
import ModalShell from './ModalShell';
import PageShell from './PageShell';
import Button from './Button';
import OverflowMenu, { type OverflowMenuItem } from './OverflowMenu';
import SaveButton from './SaveButton';
import { useDirtyTracker } from '../hooks/useDirtyTracker';
import { useToast } from '../contexts/ToastContext';
import { bgLayer1 } from '../lib/layerClasses';
import { keepEdits } from '../lib/keepEdits';
import { Icon } from './Icon';

/* Shared supplier detail view — used by tenant + platform-admin. Same
 * presentational + callback-driven contract as ExpenseDetailView. */

export interface SupplierLike {
    id?: number;
    name?: string;
    contact_name?: string | null;
    email?: string | null;
    phone?: string | null;
    website?: string | null;
    /** Our account / client code with this supplier (the code THEY gave US). */
    customer_code?: string | null;
    notes?: string | null;
}

export interface SupplierDetailLabels {
    newSupplier: string;
    editSupplier: string;
    name: string;
    namePlaceholder: string;
    comercialName: string;
    comercialNamePlaceholder: string;
    email: string;
    emailPlaceholder: string;
    phone: string;
    phonePlaceholder: string;
    website: string;
    websitePlaceholder: string;
    /** Optional — when provided, the "customer code" field is shown. */
    customerCode?: string;
    customerCodePlaceholder?: string;
    notes: string;
    notesPlaceholder: string;
    save: string;
    saving: string;
    saved: string;
    deleteSupplier: string;
    nameRequired: string;
    errSave: string;
    /** Optional read-only banner (e.g. admin's system "Polar" supplier). */
    readonlyNote?: string;
}

export interface SupplierDetailViewProps {
    supplier: SupplierLike | null;
    labels: SupplierDetailLabels;
    variant?: 'modal' | 'page';
    onClose: () => void;
    onSave: (payload: SupplierLike) => void | Promise<void>;
    onSaved?: () => void | Promise<void>;
    onDelete?: () => void;
    /**
     * Page variant only: what the host knows about the supplier beyond its
     * card (the tenant shows what was bought from it). Rendered BESIDE the
     * form on wide screens and above it on a phone; omitted, the page is the
     * form alone, as the admin uses it.
     */
    insights?: React.ReactNode;
    /** A NEW supplier starts with this name (the expense's «Crear proveedor»
     *  passes what was typed in its picker). */
    defaultName?: string;
}

const EMPTY_SUPPLIER: SupplierLike = {
    name: '', contact_name: '', email: '', phone: '', website: '', customer_code: '', notes: '',
};

const SupplierDetailView: React.FC<SupplierDetailViewProps> = ({
    supplier,
    labels,
    variant = 'modal',
    onClose,
    onSave,
    onSaved,
    onDelete,
    insights,
    defaultName,
}) => {
    const { toast } = useToast();
    const isNew = supplier === null;
    const [editable, setEditable] = useState<SupplierLike>(
        supplier || { ...EMPTY_SUPPLIER, name: defaultName ?? '' },
    );
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState<string | null>(null);

    const tracker = useDirtyTracker(editable);
    const { reset: trackerReset, markClean: trackerMarkClean } = tracker;
    // Track real changes even when creating: the empty template is the baseline,
    // so the save button stays disabled/grey until the user actually edits a
    // field (CLAUDE.md form-dirty rule). The required `name` is still validated
    // in the save handler.
    const isDirty = tracker.isDirty;

    // A prefilled name is a real change: measure from the EMPTY template, so
    // the form opens ready to save without touching another field.
    useEffect(() => {
        if (isNew && defaultName) trackerReset(EMPTY_SUPPLIER);
        // Once, on open: the prefill only exists at mount.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // A new `supplier` while mounted (a refetch): the draft takes it too,
    // keeping only what the person changed (`keepEdits`). Resetting just the
    // baseline left the form «unsaved» whenever the record differed at all.
    const lastSupplierRef = useRef(supplier);
    useEffect(() => {
        if (isNew || !supplier) return;
        const before = lastSupplierRef.current;
        lastSupplierRef.current = supplier;
        if (before !== supplier) {
            setEditable((draft) => (before && before.id === supplier.id ? keepEdits(before, supplier, draft) : supplier));
        }
        trackerReset(supplier);
    }, [supplier, isNew, trackerReset]);

    const setField = (name: keyof SupplierLike, value: string) =>
        setEditable((prev) => ({ ...prev, [name]: value }));

    const handleSaveModal = async () => {
        if (!editable.name?.trim()) { setSaveError(labels.nameRequired); return; }
        setIsSaving(true);
        try {
            await onSave(editable);
            trackerMarkClean();
            onClose();
        } catch (error) {
            toast.error(labels.errSave);
            setSaveError((error as Error).message || labels.errSave);
        } finally {
            setIsSaving(false);
        }
    };

    const handleSavePage = async (): Promise<boolean | void> => {
        if (!editable.name?.trim()) { setSaveError(labels.nameRequired); return false; }
        setSaveError(null);
        try {
            await onSave(editable);
            trackerMarkClean();
        } catch (error) {
            toast.error(labels.errSave);
            setSaveError((error as Error).message || labels.errSave);
            throw error;
        }
    };

    const Shell = variant === 'page' ? PageShell : ModalShell;

    return (
        <Shell
            isOpen
            onClose={onClose}
            dirty={isDirty}
            title={isNew ? labels.newSupplier : labels.editSupplier}
            maxWidth={variant === 'page' && insights ? 'max-w-7xl' : 'max-w-md'}
            zIndex="z-[52]"
            headerActions={
                <>
                    {variant === 'page' ? (
                        <SaveButton
                            onSave={handleSavePage}
                            onSuccess={onSaved}
                            dirty={isDirty}
                            saveLabel={labels.save}
                            savedLabel={labels.saved}
                        />
                    ) : (
                        <Button
                            onClick={handleSaveModal}
                            disabled={isSaving || !isDirty}
                            requires="write"
                            variant="primary"
                            icon={isSaving ? 'sync' : 'save'}
                        >
                            {isSaving ? labels.saving : labels.save}
                        </Button>
                    )}
                    {!isNew && onDelete && (
                        <OverflowMenu
                            items={[
                                {
                                    key: 'delete',
                                    icon: 'delete',
                                    label: labels.deleteSupplier,
                                    variant: 'danger',
                                    onClick: onDelete,
                                },
                            ] as OverflowMenuItem[]}
                        />
                    )}
                </>
            }
        >
            <div className={variant === 'page' && insights
                ? 'grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_380px] lg:items-start'
                : ''}>
            {variant === 'page' && insights ? <div className="min-w-0">{insights}</div> : null}
            <div className="space-y-6">
                {saveError && (
                    <div className="bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 rounded-xl p-4 flex items-start gap-3">
                        <Icon name="error" className="text-red-500 mt-0.5" />
                        <p className="text-red-600 dark:text-red-300 text-xs leading-snug">{saveError}</p>
                    </div>
                )}

                <section id="tour-supplier-form" className={`${bgLayer1} p-6 rounded-xl border border-border-subtle dark:border-border-subtle-dark shadow-sm space-y-4`}>
                    <SectionHeading icon="factory" className="mb-3">{labels.name}</SectionHeading>
                    {labels.readonlyNote && <p className="text-xs text-slate-500">{labels.readonlyNote}</p>}

                    <Input
                        label={labels.name}
                    markRequired
                        value={editable.name || ''}
                        onChange={(e) => setField('name', e.target.value)}
                        placeholder={labels.namePlaceholder}
                        icon="factory"
                    />
                    <Input
                        label={labels.comercialName}
                        value={editable.contact_name || ''}
                        onChange={(e) => setField('contact_name', e.target.value)}
                        placeholder={labels.comercialNamePlaceholder}
                        icon="person"
                    />
                    <div id="tour-supplier-contact" className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                            label={labels.email}
                            type="email"
                            value={editable.email || ''}
                            onChange={(e) => setField('email', e.target.value)}
                            placeholder={labels.emailPlaceholder}
                            icon="mail"
                        />
                        <Input
                            label={labels.phone}
                            value={editable.phone || ''}
                            onChange={(e) => setField('phone', e.target.value)}
                            placeholder={labels.phonePlaceholder}
                            icon="phone"
                        />
                    </div>
                    <Input
                        label={labels.website}
                        value={editable.website || ''}
                        onChange={(e) => setField('website', e.target.value)}
                        placeholder={labels.websitePlaceholder}
                        icon="language"
                    />
                    {labels.customerCode && (
                        <Input
                            label={labels.customerCode}
                            value={editable.customer_code || ''}
                            onChange={(e) => setField('customer_code', e.target.value)}
                            placeholder={labels.customerCodePlaceholder}
                            icon="badge"
                        />
                    )}
                    <Textarea
                        label={labels.notes}
                        value={editable.notes || ''}
                        onChange={(e) => setField('notes', e.target.value)}
                        rows={3}
                        placeholder={labels.notesPlaceholder}
                    />
                </section>
            </div>
            </div>
        </Shell>
    );
};

export default SupplierDetailView;
